import usePageTitle from "../utils/usePageTitle";
import { useTranslation } from "react-i18next";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Button, Container } from "common-tailwind-components";

import type { Action, BackendChatInteractiveMessage, McpRequest, NextAction } from "../api/types";
import { getWsUrl } from "../api";
import { appScrollElement } from "../utils/appScroll";

type ChatState = "idle" | "connecting" | "waiting" | "done" | "error";
type RequestStatus = "pending" | "running" | "completed" | "failed" | "cancelled";

type UiRequest = {
  request: McpRequest | null;
  isEndStream: boolean;
  status: RequestStatus;
  details: unknown | null;
};

type UiRequestGroup = {
  reason: string | null;
  actions: UiRequest[];
};

const getToolInfo = (request: McpRequest | null, isEndStream: boolean) => {
  if (isEndStream) {
    return { action: "End stream", args: null as unknown, kind: "stream" };
  }
  if (request === null) {
    return { action: "unknown", args: null as unknown, kind: "unknown" };
  }
  if (typeof request.data === "string") {
    return { action: request.data, args: null as unknown, kind: request.kind };
  }
  const [firstKey] = Object.keys(request.data);
  const action = firstKey ?? "unknown";
  const args = firstKey ? (request.data as Record<string, unknown>)[firstKey] : null;
  return { action, args, kind: request.kind };
};

const normalizeStatus = (value: unknown): RequestStatus => {
  if (typeof value !== "string") {
    return "pending";
  }
  const lowered = value.toLowerCase();
  if (lowered === "pending") return "pending";
  if (lowered === "running") return "running";
  if (lowered === "success" || lowered === "completed") return "completed";
  if (lowered === "error" || lowered === "failed") return "failed";
  if (lowered === "cancelled") return "cancelled";
  return "pending";
};

const asObject = (value: unknown): Record<string, unknown> | null => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const parseSingleAction = (item: unknown): UiRequest => {
  const action = asObject(item) as (Partial<Action> & { status?: unknown }) | null;
  const actionRequest = action?.request;
  if (action && actionRequest !== undefined) {
    if (actionRequest === "end_stream") {
      return {
        request: null,
        isEndStream: true,
        status: normalizeStatus(action.state),
        details: action.result ?? null,
      };
    }
    const streamObj = asObject(actionRequest as unknown);
    if (streamObj && "request" in streamObj) {
      return {
        request: streamObj.request as McpRequest,
        isEndStream: false,
        status: normalizeStatus(action.state),
        details: action.result ?? null,
      };
    }
  }

  const raw = item as McpRequest & { status?: unknown };
  return {
    request: raw as McpRequest,
    isEndStream: false,
    status: normalizeStatus(raw.status),
    details: null,
  };
};

const parseUiRequestGroups = (payload: unknown): UiRequestGroup[] => {
  if (!Array.isArray(payload)) {
    return [];
  }

  const hasGroupedShape = payload.some(entry => {
    const obj = asObject(entry);
    return obj !== null && "actions" in obj;
  });

  if (!hasGroupedShape) {
    return [{ reason: null, actions: payload.map(parseSingleAction) }];
  }

  return payload.map(group => {
    const obj = asObject(group);
    const actions = Array.isArray(obj?.actions) ? obj.actions : [];
    const reason = typeof obj?.reason === "string" ? obj.reason : null;
    return { reason, actions: actions.map(parseSingleAction) };
  });
};

const parseWsPayload = (payload: unknown): { groups: UiRequestGroup[]; message: string | null } => {
  if (Array.isArray(payload)) {
    return { groups: parseUiRequestGroups(payload), message: null };
  }

  const obj = asObject(payload);
  if (!obj || typeof obj.kind !== "string" || !("data" in obj)) {
    if (!obj || typeof obj.kind !== "string") {
      throw new Error("Invalid websocket payload");
    }
  }

  const typed = obj as BackendChatInteractiveMessage;
  if (typed.kind === "NewActions") {
    return { groups: parseUiRequestGroups(typed.data), message: null };
  }
  if (typed.kind === "AlreadyTerminalState") {
    return { groups: [], message: "The selected run is already in a terminal state." };
  }
  if (typed.kind === "WaitForConfirmation") {
    return { groups: [], message: "This workflow run is being handled elsewhere. Retry shortly." };
  }

  throw new Error(`Unknown websocket payload kind: ${obj.kind}`);
};

const formatArgValue = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || value === null) {
    return String(value);
  }
  if (typeof value === "bigint") return value.toString();
  if (Array.isArray(value) || typeof value === "object") {
    return JSON.stringify(value);
  }
  return "unknown";
};

const statusClasses: Record<RequestStatus, { row: string; badge: string }> = {
  pending: {
    row: "border-warning/30 bg-warning/5",
    badge: "border-warning/40 bg-warning/10 text-warning-strong",
  },
  running: {
    row: "border-info/30 bg-info/5",
    badge: "border-info/40 bg-info/10 text-info-strong",
  },
  completed: {
    row: "border-success/30 bg-success/5",
    badge: "border-success/40 bg-success/10 text-success-strong",
  },
  failed: {
    row: "border-danger/30 bg-danger/5",
    badge: "border-danger/40 bg-danger/10 text-danger-strong",
  },
  cancelled: {
    row: "border-outline bg-surface-strong",
    badge: "border-outline bg-muted-surface text-text-dim",
  },
};

const Request = ({
  requestIdx,
  request,
  onRunUntil,
  disabled,
}: {
  requestIdx: number;
  request: UiRequest;
  onRunUntil: (idx: number) => void;
  disabled: boolean;
}) => {
  const { action, args, kind } = getToolInfo(request.request, request.isEndStream);
  const styles = statusClasses[request.status];
  const hasObjectArgs = args !== null && typeof args === "object" && !Array.isArray(args);

  return (
    <li className={`rounded-xl border p-4 ${styles.row}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-text-dim">{kind}</p>
          <p className="text-sm font-semibold text-text">
            {requestIdx + 1}. {action}
          </p>
        </div>
      </div>

      {!request.isEndStream && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${styles.badge}`}
          >
            {request.status}
          </span>
          {request.status === "pending" && (
            <Button
              color="secondary"
              type="button"
              disabled={disabled}
              onClick={() => onRunUntil(requestIdx)}
            >
              Run until here
            </Button>
          )}
        </div>
      )}

      {request.isEndStream ? (
        <div className="mt-3 rounded-lg border border-success/30 bg-success/10 p-3">
          <p className="text-[11px] uppercase tracking-wide text-success-strong">
            Stream completion marker
          </p>
          <p className="mt-1 text-xs text-text">Backend signaled the end of the stream.</p>
        </div>
      ) : (
        <div className="mt-3 rounded-lg border border-outline bg-surface-strong p-3">
          <p className="text-[11px] uppercase tracking-wide text-text-dim">Arguments</p>
          {args === null && <p className="mt-1 text-xs text-text-dim">No arguments</p>}
          {hasObjectArgs && (
            <dl className="mt-2 grid gap-2 sm:grid-cols-2">
              {Object.entries(args as Record<string, unknown>).map(([argName, value]) => (
                <div key={argName} className="rounded border border-outline bg-surface p-2">
                  <dt className="text-[11px] uppercase tracking-wide text-text-dim">{argName}</dt>
                  <dd className="mt-1 break-all text-xs text-text">{formatArgValue(value)}</dd>
                </div>
              ))}
            </dl>
          )}
          {!hasObjectArgs && args !== null && (
            <p className="mt-2 break-all text-xs text-text">{formatArgValue(args)}</p>
          )}
        </div>
      )}

      {request.details !== null && (
        <div className="mt-3 rounded-lg border border-outline bg-surface-strong p-3">
          <p className="text-[11px] uppercase tracking-wide text-text-dim">Details</p>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-xs text-text">
            {JSON.stringify(request.details, null, 2)}
          </pre>
        </div>
      )}
    </li>
  );
};

const ChatInteractive = () => {
  const { t } = useTranslation();
  usePageTitle(t("interactive_chat"));
  const wsRef = useRef<WebSocket | null>(null);
  const requestIdRef = useRef(0);
  const [prompt, setPrompt] = useState("");
  const [state, setState] = useState<ChatState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<UiRequestGroup[]>([]);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);

  const hasEndStream = groups.some(group => group.actions.some(request => request.isEndStream));
  const totalRequests = groups.reduce((acc, group) => acc + group.actions.length, 0);
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      // The app's scroll container, not the window: the window does not scroll.
      const scroller = appScrollElement();
      scroller?.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    });
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = prompt.trim();
    if (!text) {
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    let responseReceived = false;

    if (wsRef.current) {
      wsRef.current.close();
    }

    setGroups([]);
    setError(null);
    setButtonsDisabled(false);
    setState("connecting");

    const ws = new WebSocket(getWsUrl("/ws/chat-interactive/"));
    wsRef.current = ws;

    ws.onopen = () => {
      if (requestId !== requestIdRef.current) {
        return;
      }
      ws.send(JSON.stringify({ text }));
      setState("waiting");
    };

    ws.onmessage = message => {
      if (requestId !== requestIdRef.current) {
        return;
      }
      try {
        const parsed = parseWsPayload(JSON.parse(message.data));
        responseReceived = true;
        if (parsed.groups.length > 0) {
          setGroups(parsed.groups);
        }
        scrollToBottom();
        setState("done");
        setButtonsDisabled(false);
        setError(parsed.message);
      } catch (parseError) {
        console.error(parseError);
        setButtonsDisabled(false);
        setError("Failed to parse websocket response.");
        setState("error");
      }
    };

    ws.onerror = () => {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setButtonsDisabled(false);
      setError("Websocket error while contacting /ws/chat-interactive/.");
      setState("error");
    };

    ws.onclose = closeEvent => {
      if (requestId !== requestIdRef.current) {
        return;
      }
      wsRef.current = null;
      setButtonsDisabled(false);
      if (responseReceived) {
        return;
      }
      if (!closeEvent.reason) {
        setError("Connection closed before a response was received.");
      } else {
        setError(`Connection closed: ${closeEvent.reason}`);
      }
      setState("error");
    };
  };

  return (
    <Container>
      <div className="mx-auto max-w-5xl space-y-6 pb-12 pt-7">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-text">Interactive Chat</h1>
          <p className="text-sm text-text-dim">
            Send a prompt, receive generated MCP requests, and inspect each request before
            execution.
          </p>
        </div>

        <form
          className="space-y-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm"
          onSubmit={onSubmit}
        >
          <textarea
            value={prompt}
            onChange={event => setPrompt(event.target.value)}
            placeholder="Describe what you want to do..."
            rows={6}
            className="w-full rounded-xl border border-outline bg-surface p-3 text-sm text-text outline-none transition focus:border-primary"
          />
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-text-dim">
              {state === "idle" ? "Idle" : null}
              {state === "connecting" ? "Opening websocket..." : null}
              {state === "waiting" ? "Waiting for backend response..." : null}
              {state === "done" ? "Response received." : null}
              {state === "error" ? "Failed." : null}
            </p>
            <Button
              color="primary"
              type="submit"
              loading={state === "connecting" || state === "waiting"}
              disabled={state === "connecting" || state === "waiting" || prompt.trim().length === 0}
            >
              Submit
            </Button>
          </div>
        </form>

        {error && (
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger-strong">
            {error}
          </div>
        )}

        {groups.length > 0 && (
          <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-text">Planned requests</h2>
            <p className="mb-4 mt-1 text-xs text-text-dim">
              {groups.length} group(s), {totalRequests} request(s)
            </p>

            {hasEndStream && (
              <div className="mb-4 rounded-xl border border-success/40 bg-success/10 p-3 text-sm text-success-strong">
                End-of-stream marker received. Conversation flow completed successfully.
              </div>
            )}

            <div className="space-y-4">
              {groups.map((group, groupIdx) => {
                const isLatestGroup = groupIdx === groups.length - 1;
                return (
                  <section
                    key={`group-${groupIdx}`}
                    className="rounded-xl border border-outline bg-surface-strong p-4"
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-text">Group {groupIdx + 1}</h3>
                      <span className="text-xs text-text-dim">
                        {group.actions.length} request(s)
                      </span>
                    </div>

                    {group.reason && (
                      <div className="mb-3 rounded-lg border border-info/40 bg-info/10 p-3">
                        <p className="text-[11px] uppercase tracking-wide text-info-strong">
                          Reasoning summary
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-xs text-text">{group.reason}</p>
                      </div>
                    )}

                    {group.actions.length === 0 ? (
                      <p className="text-xs text-text-dim">No tool calls in this group.</p>
                    ) : (
                      <ol className="space-y-3">
                        {group.actions.map((request, requestIdx) => (
                          <Request
                            key={`group-${groupIdx}-request-${requestIdx}`}
                            requestIdx={requestIdx}
                            request={request}
                            disabled={buttonsDisabled || !isLatestGroup}
                            onRunUntil={idx => {
                              if (!wsRef.current || !isLatestGroup) {
                                return;
                              }
                              setButtonsDisabled(true);
                              wsRef.current.send(
                                JSON.stringify({ run_until: idx } satisfies NextAction),
                              );
                            }}
                          />
                        ))}
                      </ol>
                    )}
                  </section>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </Container>
  );
};

export default ChatInteractive;
